// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * D-SUPPORT-15 — Support tools.
 *
 * Patrick, 2026-08-22: "add the support tools under support and feedback, this is going to be our
 * placeholder for all tier 1 support tools."
 *
 * A HUB, not another nav row. The Support & feedback section already carries eight entries; if
 * every tool earned its own, the sidebar would become the tool list and stop being navigation.
 * Tools live in the TOOLS registry below and appear here — adding one is a single array entry
 * plus a route, and the sidebar never grows.
 *
 * Tools are filtered by permission, the same way the sidebar filters nav. A tool an agent cannot
 * open is not shown to an agent: a visible card that 403s is worse than an absent one, because it
 * reads as broken rather than as out of scope.
 */
import { useNavigate } from 'react-router-dom'
import { MailWarning, Wrench, ChevronRight, Bot, Settings, MessageSquare, ScrollText, FileJson } from 'lucide-react'
import { useAuth } from '../../auth/AuthContext'

type SupportTool = {
  key: string
  label: string
  /** What question the tool answers. Written as the question, not as a feature name. */
  description: string
  icon: React.ComponentType<{ size?: number | string }>
  route: string
  /** Same permission the route itself enforces — the card must not promise more than the page allows. */
  requiredPermission: string
}

// ADD TIER-1 TOOLS HERE. Keep the description phrased as the question the tool answers; a support
// agent scanning this page is looking for "which of these tells me what I need", not for a
// feature inventory.
//
// Permissions are NOT uniform across these, and that is deliberate rather than an oversight.
// Feedback and the audit log are platform-admin surfaces (admin.feedback.read / admin.audit.read)
// that no support tier currently holds — gap 5 in D-SUPPORT-11, a policy question for Sasha, not
// a coding one. Listing them here with their real permission means a supervisor simply does not
// see them, and the day that policy changes they appear with no code change. Hard-coding them
// visible would produce a card that 403s, which reads as broken rather than as out of scope.
const TOOLS: SupportTool[] = [
  {
    key: 'notification-delivery',
    label: 'Notification delivery',
    description: 'Did the case notification actually reach anyone? Shows failed and suppressed emails with the reason — queued is not delivered.',
    icon: MailWarning,
    route: '/admin/support/notification-delivery',
    // An agent working a case needs to know whether the customer was actually told. That is
    // operational, not managerial, and it shows the same recipient addresses the queue already does.
    requiredPermission: 'support.tickets.work',
  },
  {
    key: 'pioneer-lookup',
    label: 'Pioneer Rx lookup',
    description: 'Paste an Rx number: every PioneerRx envelope we received for it, what each one said, and what the portal shows for the same Rx.',
    icon: FileJson,
    // Its OWN permission, not support.tickets.work. This is the only tool here that returns patient
    // data, so the grant has to be visible and revocable on its own rather than riding along with
    // the queue. See migration 366.
    requiredPermission: 'support.pioneer.read',
    route: '/admin/support/pioneer',
  },
  {
    key: 'ai-assistant',
    label: 'AI assistant',
    description: 'Ask about tickets, queue load and platform health in plain language, with the answers drawn from live data rather than guessed.',
    icon: Bot,
    route: '/admin/support/ai',
    requiredPermission: 'support.tickets.work',
  },
  {
    key: 'feedback',
    label: 'Feedback',
    description: 'What are customers telling us outside a support case? Triage and respond to submitted feedback.',
    icon: MessageSquare,
    // Support-scoped route and a non-admin-prefixed key, for the same reason as the audit tile.
    route: '/admin/support/feedback',
    requiredPermission: 'support.feedback.manage',
  },
  {
    key: 'audit-log',
    label: 'Audit log',
    description: 'Who did what, system-wide. The append-only record — use it when "it changed and nobody knows why" needs an answer.',
    icon: ScrollText,
    // Support-scoped route, not the admin tab: an agent reaching /admin/audit would be entering the
    // admin shell. Gated on support.audit.read — granting admin.audit.read instead would have
    // matched the has('admin.') prefix in BOTH legacy-role derivers and promoted the account to the
    // admin bucket, past every RequireOneOf('admin') gate. See migration 367.
    route: '/admin/support/audit',
    requiredPermission: 'support.audit.read',
  },
  {
    key: 'support-settings',
    label: 'Support settings',
    description: 'SLA targets, business hours, macros and AI configuration. Manager tier — these change behaviour for the whole team.',
    icon: Settings,
    route: '/admin/support/settings',
    requiredPermission: 'support.settings.manage',
  },
]

export default function SupportToolsPage() {
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const visible = TOOLS.filter(t => hasPermission(t.requiredPermission))

  return (
    <div style={{ padding: '24px 24px 48px', maxWidth: 'var(--page-max, 1280px)', margin: '0 auto', width: '100%' }}>
      <header style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Wrench size={21} /> Support tools
        </h1>
        <div style={{ color: 'var(--color-text-mute)', fontSize: 13, marginTop: 4 }}>
          Tier-1 tools for answering questions the queue cannot. Tools you cannot open at your
          tier are not shown.
        </div>
      </header>

      {visible.length === 0 ? (
        <div style={{ ...card, color: 'var(--color-text-mute)', fontSize: 13 }}>
          No tools available at your tier. Support tools require the supervisor tier or above.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {visible.map(t => {
            const Icon = t.icon
            return (
              <button key={t.key} onClick={() => navigate(t.route)}
                style={{ ...card, textAlign: 'left', cursor: 'pointer', display: 'flex', gap: 12,
                         alignItems: 'flex-start', font: 'inherit', color: 'var(--color-text)' }}>
                <span style={{ color: '#0e7490', flexShrink: 0, marginTop: 2 }}><Icon size={20} /></span>
                <span style={{ flex: 1 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 14.5 }}>
                    {t.label}
                    <ChevronRight size={15} aria-hidden="true" />
                  </span>
                  <span style={{ display: 'block', fontSize: 12.5, color: 'var(--color-text-mute)', marginTop: 4, lineHeight: 1.5 }}>
                    {t.description}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

const card: React.CSSProperties = {
  background: 'var(--color-surface)', border: '1px solid var(--color-border)',
  borderRadius: 12, padding: 14,
}
